import { useEffect, useRef, useState } from 'react';
import { readWeather, type WeatherSnapshot } from '../../adapters/weather-http';
import { stationSelection } from './weather-selection';

export function useLiveWeather(enabled: boolean) {
  const [state, setState] = useState<{ data: WeatherSnapshot | null; unavailable: boolean }>({
    data: null,
    unavailable: false,
  });
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const read = async () => {
      try {
        const data = await readWeather(controller.signal);
        if (!controller.signal.aborted) setState({ data, unavailable: false });
      } catch {
        if (!controller.signal.aborted) setState({ data: null, unavailable: true });
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(() => void read(), 30000);
      }
    };
    void read();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [enabled]);
  return enabled ? state : { data: null, unavailable: false };
}
export function LiveWeather({
  data,
  unavailable,
  areaId,
  focus,
  selectedId = '',
}: {
  data: WeatherSnapshot | null;
  unavailable: boolean;
  areaId: string;
  focus: (longitude: number, latitude: number, id: string) => void;
  selectedId?: string;
}) {
  const section = useRef<HTMLElement>(null);
  const hasSelection = !!data?.sources.some((s) => selectedId === stationSelection(s.sourceId));
  useEffect(() => {
    if (hasSelection) section.current?.scrollIntoView({ block: 'start' });
  }, [selectedId, hasSelection]);
  const sources = data?.sources.filter((s) => !areaId || s.region.areaId === areaId) ?? [];
  return (
    <section ref={section} className="risk-empty" aria-label="真实天气观测" data-vs11-weather>
      <strong>LIVE · 真实天气观测</strong>
      <p>独立天气输入 · 不计入合成预警或历史科研 · 不代表火灾</p>
      {unavailable ? (
        <p role="status">天气读取不可用 / 未配置，不能解释为正常。</p>
      ) : !data ? (
        <p>正在读取天气…</p>
      ) : (
        <>
          <p>
            全实例累计 {data.total} 条 LIVE 指标观测；当前区域 {sources.length} 个来源。
          </p>
          {sources.map((s) => {
            const health = data.health.find((h) => h.sourceId === s.sourceId);
            const observations = data.observations.filter((o) => o.input.sourceId === s.sourceId);
            const point = observations.find((o) => o.locationStatus === 'LOCATED');
            return (
              <details key={s.sourceId} open={selectedId === stationSelection(s.sourceId)}>
                <summary>
                  {s.region.displayName} · {health?.status ?? 'UNAVAILABLE'}
                </summary>
                <p>
                  STATION_OBSERVATION · {s.sourceId} · LIVE
                  <br />
                  质量 {health?.quality} · 连续失败 {health?.consecutiveFailures ?? 0} ·{' '}
                  {health?.lastProviderErrorCategory}
                </p>
                <p>下次采集 / 重试 {health?.nextRetryAt ?? '未知'}</p>
                {observations.map((o) => (
                  <p key={o.reference}>
                    {o.input.metric} {o.input.value.toFixed(1)} {o.input.unit} · {o.input.quality}
                    <br />
                    观测 {o.input.observedAt}
                    <br />
                    接收 {o.receivedAt}
                  </p>
                ))}
                {point ? (
                  <button
                    type="button"
                    onClick={() =>
                      focus(point.longitude!, point.latitude!, stationSelection(s.sourceId))
                    }
                  >
                    定位真实站点
                  </button>
                ) : (
                  <p>UNLOCATED · 没有可信坐标</p>
                )}
              </details>
            );
          })}
        </>
      )}
      <a
        href="https://www.weather.gov/documentation/services-web-api"
        target="_blank"
        rel="noreferrer"
      >
        来源：NOAA/NWS（平台规范化显示）
      </a>
    </section>
  );
}
